import { useEffect, useState } from 'react';

import { usePathname } from '@/navigation';

/**
 * Custom hook to manage restricted cart mode state.
 *
 * This hook:
 * - Checks if the user is on the cart page
 * - Reads the restricted-cart-mode flag from sessionStorage
 * - Clears the flag when navigating away from the cart page
 * - Listens for changes to the flag via custom events
 *
 * @returns {boolean} Whether restricted cart mode is active
 */
export const useRestrictedCartMode = (): boolean => {
  const pathname = usePathname();

  // Initialize from sessionStorage to prevent flicker on first render
  const [isRestrictedCart, setIsRestrictedCart] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    const isOnCartPage = pathname?.includes('/cart');
    if (!isOnCartPage) {
      return false;
    }
    return sessionStorage.getItem('restricted-cart-mode') === 'true';
  });

  useEffect(() => {
    const checkRestrictedMode = () => {
      // Only check flag if we're on the cart page
      const isOnCartPage = pathname?.includes('/cart');

      if (!isOnCartPage) {
        // Clear flag if we're not on cart page
        if (typeof window !== 'undefined') {
          sessionStorage.removeItem('restricted-cart-mode');
        }
        setIsRestrictedCart(false);
        return;
      }

      const restrictedMode =
        typeof window !== 'undefined' &&
        sessionStorage.getItem('restricted-cart-mode') === 'true';
      setIsRestrictedCart(restrictedMode);
    };

    checkRestrictedMode();

    // Listen for custom event (same window only - sessionStorage is tab-scoped)
    window.addEventListener('restricted-cart-mode-change', checkRestrictedMode);

    return () => {
      window.removeEventListener(
        'restricted-cart-mode-change',
        checkRestrictedMode,
      );
    };
  }, [pathname]);

  return isRestrictedCart;
};
